import apiService from './api';

export interface UserSession {
  id: string;
  session_key: string;
  device_name: string;
  device_type: 'desktop' | 'mobile' | 'tablet' | 'unknown';
  browser: string;
  os: string;
  ip_address: string;
  location: string;
  is_active: boolean;
  created_at: string;
  last_activity: string;
  expires_at: string;
  device_display_name: string;
  time_since_activity: string;
  is_current_session: boolean;
  is_expired: boolean;
}

export interface DeviceInfo {
  device_type: string;
  device_name: string;
  browser: string;
  os: string;
  ip_address?: string;
  user_agent: string;
  location?: string;
  session_key?: string;
}

export interface SessionsResponse {
  sessions: UserSession[];
  total: number;
  current_session_key: string;
}

const CLIENT_SESSION_KEY_STORAGE = 'client_session_key';

class SessionsService {
  private baseUrl = '/user-sessions';
  private activityIntervalId: ReturnType<typeof setInterval> | null = null;

  getClientSessionKey(): string {
    let key = localStorage.getItem(CLIENT_SESSION_KEY_STORAGE);
    if (!key) {
      key = crypto.randomUUID();
      localStorage.setItem(CLIENT_SESSION_KEY_STORAGE, key);
    }
    return key;
  }

  clearClientSessionKey(): void {
    localStorage.removeItem(CLIENT_SESSION_KEY_STORAGE);
  }

  private withSessionPayload<T extends object>(payload: T): T & { session_key: string } {
    return {
      ...payload,
      session_key: this.getClientSessionKey(),
    };
  }

  async getActiveSessions(): Promise<SessionsResponse> {
    try {
      const response = await apiService.get(`${this.baseUrl}/sessions/`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erreur lors de la récupération des sessions');
    }
  }

  async createOrUpdateSession(deviceInfo: DeviceInfo): Promise<UserSession> {
    try {
      const response = await apiService.post(
        `${this.baseUrl}/sessions/`,
        this.withSessionPayload(deviceInfo),
      );
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la création/mise à jour de la session:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors de la création de la session');
    }
  }

  async terminateSession(sessionId: string): Promise<void> {
    try {
      await apiService.delete(`${this.baseUrl}/sessions/${sessionId}/`);
    } catch (error: any) {
      console.error('Erreur lors de la terminaison de la session:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors de la terminaison de la session');
    }
  }

  async terminateAllOtherSessions(): Promise<{ message: string; terminated_count: number }> {
    try {
      const response = await apiService.post(`${this.baseUrl}/sessions/terminate_all_others/`);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la terminaison des autres sessions:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors de la terminaison des sessions');
    }
  }

  async deactivateCurrentSession(): Promise<void> {
    try {
      await apiService.post(`${this.baseUrl}/sessions/deactivate_current/`);
    } catch (error: any) {
      console.warn('Impossible de désactiver la session courante:', error?.response?.data || error);
    } finally {
      this.stopActivityUpdates();
    }
  }

  async updateCurrentSessionActivity(): Promise<{ message: string; last_activity: string }> {
    try {
      const response = await apiService.post(`${this.baseUrl}/sessions/update_activity/`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erreur lors de la mise à jour de l\'activité');
    }
  }

  async getCurrentSession(): Promise<UserSession> {
    try {
      const response = await apiService.get(`${this.baseUrl}/sessions/current_session/`);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de la récupération de la session actuelle:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors de la récupération de la session actuelle');
    }
  }

  async cleanupExpiredSessions(): Promise<{ message: string; cleaned_count: number }> {
    try {
      const response = await apiService.post(`${this.baseUrl}/sessions/cleanup_expired/`);
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors du nettoyage des sessions:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors du nettoyage des sessions');
    }
  }

  detectDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent;

    let deviceType = 'unknown';
    if (/Mobile|Android|iPhone|iPad/i.test(userAgent)) {
      deviceType = /iPad|Tablet/i.test(userAgent) ? 'tablet' : 'mobile';
    } else if (/Windows|Mac|Linux/i.test(userAgent)) {
      deviceType = 'desktop';
    }

    let browser = 'Unknown';
    if (/Chrome/i.test(userAgent)) browser = 'Chrome';
    else if (/Firefox/i.test(userAgent)) browser = 'Firefox';
    else if (/Safari/i.test(userAgent)) browser = 'Safari';
    else if (/Edge/i.test(userAgent)) browser = 'Edge';

    let os = 'Unknown';
    if (/Windows/i.test(userAgent)) os = 'Windows';
    else if (/Mac OS/i.test(userAgent)) os = 'macOS';
    else if (/Linux/i.test(userAgent)) os = 'Linux';
    else if (/Android/i.test(userAgent)) os = 'Android';
    else if (/iPhone|iPad/i.test(userAgent)) os = 'iOS';

    let deviceName = `${os} - ${browser}`;
    if (deviceType === 'mobile') deviceName = `Mobile (${os})`;
    else if (deviceType === 'tablet') deviceName = `Tablette (${os})`;

    return {
      device_type: deviceType,
      device_name: deviceName,
      browser,
      os,
      user_agent: userAgent,
    };
  }

  async initializeSession(): Promise<UserSession | null> {
    try {
      const deviceInfo = this.detectDeviceInfo();
      const session = await this.createOrUpdateSession(deviceInfo);
      this.scheduleActivityUpdates();
      return session;
    } catch (error) {
      console.error('Erreur lors de l\'initialisation de la session:', error);
      return null;
    }
  }

  private scheduleActivityUpdates(): void {
    if (this.activityIntervalId) {
      clearInterval(this.activityIntervalId);
    }

    this.activityIntervalId = setInterval(async () => {
      try {
        await this.updateCurrentSessionActivity();
      } catch (error) {
        console.error('Erreur lors de la mise à jour périodique de l\'activité:', error);
      }
    }, 5 * 60 * 1000);
  }

  stopActivityUpdates(): void {
    if (this.activityIntervalId) {
      clearInterval(this.activityIntervalId);
      this.activityIntervalId = null;
    }
  }

  async updateActivityOnInteraction(): Promise<void> {
    try {
      await this.updateCurrentSessionActivity();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'activité:', error);
    }
  }
}

const sessionsService = new SessionsService();
export default sessionsService;
